/**
 * The Candidate Requests queue's filter and ordering, held in the URL.
 *
 * Not component state, for two reasons that have both bitten this tab before: the 15-minute idle
 * reload wipes `useState` (see `useAutoRefresh`), so a recruiter who filtered to "needs you" would
 * silently get the whole queue back; and a filtered view should be linkable — "look at these three"
 * is a message people send.
 *
 * `replace: true` on every change: filtering is not a navigation step, so the back button should
 * leave the tab rather than undo a chip click one at a time.
 */
import { useSearchParams } from 'react-router-dom';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import {
  isRequestBucket,
  isRequestSort,
  type RequestBucket,
  type RequestSort,
} from './requestGroups';

export function useRequestFilters() {
  const [params, setParams] = useSearchParams();

  const bucketParam = params.get('bucket');
  const sortParam = params.get('sort');
  /**
   * UNREAD is the default view (Sofia, Sep 10 2026): the tab opens on the mail the recruiter has not
   * read yet, and "Inbound questions" (= `all`) is one click away. `?bucket=all` is therefore a real
   * URL now, and a bare URL means unread.
   */
  const bucket: RequestBucket = isRequestBucket(bucketParam) ? bucketParam : DEFAULT_BUCKET;
  const sort: RequestSort = isRequestSort(sortParam) ? sortParam : 'oldest';

  /** Defaults are absent from the URL rather than spelled out — a clean link for the normal view. */
  const setParam = (key: 'bucket' | 'sort', value: string, fallback: string) => {
    const next = new URLSearchParams(params);
    if (value === fallback) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return {
    bucket,
    sort,
    setSort: (next: RequestSort) => {
      void logAnalyticsEvent('ta_requests_sort_changed', { sort: next });
      setParam('sort', next, 'oldest');
    },
    /** A chip is a toggle: clicking the active one falls back to the whole queue instead of dead-ending. */
    toggleBucket: (next: RequestBucket) => {
      const cleared = bucket === next;
      // The bucket NAME only — never what the filtered list contains.
      void logAnalyticsEvent('ta_requests_filter_changed', { bucket: cleared ? 'all' : next, cleared });
      setParam('bucket', cleared ? 'all' : next, DEFAULT_BUCKET);
    },
  };
}

export const DEFAULT_BUCKET: RequestBucket = 'unread';
